<template>
  <section class="card">
    <h2>表单管理</h2>
    <p v-if="!isFixedFormMode" class="tip">“新增表单”用于创建一整套业务表单（如入会申请、供需发布、活动报名）；字段是这套表单里的具体输入项。单选/下拉/多选字段支持按“每行一个选项”可视化维护。</p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>
    <div v-if="isFixedFormMode && fixedFormMissing && !currentForm" class="fixed-init-card">
      <p class="fixed-init-text">未找到固定表单 <code>{{ fixedFormCode }}</code>，可先初始化后再维护字段分组与 Word 模板。</p>
      <button class="btn btn-primary" :disabled="fixedInitBusy || !canFormEdit" @click="initFixedForm">
        {{ fixedInitBusy ? '初始化中...' : fixedInitBtnTextDefault() }}
      </button>
    </div>
    <button v-if="!isFixedFormMode" class="btn btn-primary" :disabled="!canFormEdit" @click="openCreateForm">新增表单</button>
    <table v-if="!isFixedFormMode">
      <thead>
        <tr><th>序号</th><th>名称</th><th>状态</th><th>版本</th><th>操作</th></tr>
      </thead>
      <tbody>
        <tr v-for="(item, idx) in pagedDisplayForms" :key="item.id">
          <td>{{ formsRowOffset + idx + 1 }}</td>
          <td>{{ item.name }}</td>
          <td>{{ statusText(item.statusCode) }}</td>
          <td>{{ item.version }}</td>
          <td>
            <button class="btn btn-edit" :disabled="!canFormEdit" @click="openEditForm(item)">编辑</button>
            <button class="btn btn-edit" @click="loadFields(item)">字段</button>
            <button class="btn btn-delete" :disabled="!canFormDelete" @click="onDeleteForm(item)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>
    <ListPager
      v-if="!isFixedFormMode"
      v-model:page="formsPage"
      v-model:page-size="formsPageSize"
      :total="displayForms.length"
    />

    <div v-if="currentForm" class="field-card">
      <h3>{{ currentForm.name }}</h3>
      <div class="top-config-grid">
        <div class="group-card">
          <div class="group-head">
            <h4>分组标题管理（用于小程序蓝色大标题）</h4>
            <small>先维护分组和顺序，再在字段里下拉选择，避免手输出错。</small>
          </div>
          <div class="group-actions">
            <input v-model.trim="groupDraftTitle" placeholder="新增分组标题，如：单位信息" />
          <button class="btn btn-primary" @click="addGroupItem">添加分组</button>
            <button class="btn btn-edit" @click="persistGroupCatalog">保存分组顺序</button>
          </div>
          <div v-if="groupCatalog.length" class="group-list">
            <div v-for="(g, idx) in groupCatalog" :key="g.id || g.code || idx" class="group-item">
              <input
                v-if="groupRenameIdx === idx"
                v-model.trim="groupRenameDraft"
                class="group-rename-input"
                placeholder="分组标题"
                @keyup.enter="confirmRenameGroupItem"
                @keyup.esc="cancelRenameGroupItem"
              />
              <span v-else class="group-item-title">{{ g.title }}</span>
              <div class="group-item-ops">
                <template v-if="groupRenameIdx === idx">
                  <button class="btn btn-primary" type="button" :disabled="!canFormEdit" @click="confirmRenameGroupItem">保存</button>
                  <button class="btn btn-edit" type="button" @click="cancelRenameGroupItem">取消</button>
                </template>
                <template v-else>
                  <button class="btn btn-edit" type="button" :disabled="!canFormEdit" @click="startRenameGroupItem(idx)">重命名</button>
                  <button class="btn btn-edit" type="button" :disabled="idx === 0" @click="moveGroupItem(idx, -1)">上移</button>
                  <button class="btn btn-edit" type="button" :disabled="idx === groupCatalog.length - 1" @click="moveGroupItem(idx, 1)">下移</button>
                  <button class="btn btn-delete" type="button" :disabled="!canFormDelete" @click="removeGroupItem(idx)">删除</button>
                </template>
              </div>
            </div>
          </div>
          <p v-else class="group-empty">暂无分组，字段会默认归到“其他信息”。</p>
        </div>
        <div v-if="showExportConfig" class="export-card">
          <h4>Word 导出配置</h4>
          <p class="export-tip">
            模板仅支持 .docx，正文中使用 <code v-pre>{{字段key}}</code> 占位。
          </p>
          <div class="export-row">
            <span class="export-label">Word 模板</span>
            <span class="export-val">{{ (currentForm && currentForm.wordTemplatePath) || '未上传' }}</span>
            <label class="btn btn-edit export-file">
              上传 .docx
              <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" class="export-file-input" @change="onWordTemplateFile" />
            </label>
          </div>
        </div>
      </div>
      <div class="filters filters-panel field-filters">
        <div class="field-filters-main">
          <div class="flt">
            <input
              v-model.trim="fieldSearchKeyword"
              class="flt-input flt-input--wide"
              placeholder="字段名称"
              aria-label="字段名称"
            />
          </div>
          <div class="flt">
            <FancySelect
              v-model="fieldFilterType"
              icon="▦"
              :options="fieldFilterTypeOptions"
              placeholder="类型"
              class="flt-select"
              :searchable="true"
            />
          </div>
          <div class="flt">
            <FancySelect
              v-model="fieldFilterGroup"
              icon="☰"
              :options="fieldFilterGroupOptions"
              placeholder="分组"
              class="flt-select"
              :searchable="true"
            />
          </div>
          <div class="flt">
            <FancySelect
              v-model="fieldFilterRequired"
              icon="!"
              :options="fieldFilterRequiredOptions"
              placeholder="是否必填"
              class="flt-select"
            />
          </div>
          <div class="filter-actions">
            <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFieldFilters">重置</button>
          </div>
        </div>
        <div class="field-filters-bulk">
          <span class="bulk-tip">已选 {{ selectedFieldCount }} 个字段</span>
          <FancySelect
            v-model="bulkGroupTitle"
            icon="☰"
            :options="groupSelectOptions"
            placeholder="批量归属到分组…"
            :searchable="true"
            class="bulk-select"
          />
          <button type="button" class="btn btn-primary bulk-btn" :disabled="bulkBusy || !selectedFieldCount" @click="applyBulkGroup">
            {{ bulkBusy ? '处理中…' : '批量归组' }}
          </button>
          <button type="button" class="btn btn-edit bulk-btn" :disabled="bulkBusy || !selectedFieldCount" @click="clearFieldSelection">清空选择</button>
        </div>
      </div>
      <p class="field-id-tip">
        列表「序号」按当前显示顺序从 1 自增；拖拽可调整字段顺序。表单编码、字段标识与排序值由系统自动维护，无需填写数据库 ID。
      </p>
      <table>
        <thead>
          <tr>
            <th class="col-center col-tight" style="width: 44px;">拖拽</th>
            <th class="col-center col-tight col-check">
              <input type="checkbox" :checked="allFieldsSelected" @change="toggleSelectAllFields" />
            </th>
            <th class="col-center col-tight" style="width: 52px;">序号</th>
            <th class="col-center">字段名称</th>
            <th class="col-center">类型</th>
            <th class="col-center">分组</th>
            <th class="col-center">必填</th>
            <th class="col-center col-ops">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(f, idx) in pagedFilteredFields"
            :key="f.id"
            :draggable="canFormEdit && !sortBusy"
            :class="{ 'row-draggable': canFormEdit, 'row-dragging': dragFieldId === f.id }"
            @dragstart="onDragStartField($event, f, fieldsRowOffset + idx)"
            @dragover.prevent="onDragOverField($event, f, fieldsRowOffset + idx)"
            @drop.prevent="onDropField($event, f, fieldsRowOffset + idx)"
            @dragend="onDragEndField"
          >
            <td class="move-cell col-center col-tight">
              <span class="drag-hint">⋮⋮</span>
            </td>
            <td class="col-center col-tight col-check">
              <input type="checkbox" :checked="isFieldSelected(f.id)" @change="toggleFieldSelection(f.id)" />
            </td>
            <td class="col-center col-tight">{{ fieldsRowOffset + idx + 1 }}</td>
            <td class="col-center">{{ f.label }}</td>
            <td class="col-center">{{ f.fieldType }}</td>
            <td class="col-center">{{ groupNameOfField(f) }}</td>
            <td class="col-center">{{ f.requiredField ? '是' : '否' }}</td>
            <td class="col-center col-ops">
              <button class="btn btn-edit" :disabled="!canFormEdit" @click="openEditField(f)">编辑</button>
              <button class="btn btn-delete" :disabled="!canFormDelete" @click="onDeleteField(f)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="table-add-row">
        <button
          class="add-icon-btn"
          :disabled="!canFormEdit"
          title="新增字段"
          aria-label="新增字段"
          @click="openCreateField"
        >
          新增字段
        </button>
      </div>
      <ListPager
        v-model:page="fieldsPage"
        v-model:page-size="fieldsPageSize"
        :total="filteredFields.length"
      />
    </div>

    <div v-if="showFormDialog" class="mask" @click.self="showFormDialog = false">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ formEditId ? '编辑表单' : '新增表单' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showFormDialog = false">×</button>
        </div>
        <div class="dialog-body">
        <div class="form-grid">
          <label v-if="formEditId" class="field">
            <span>表单编码：</span>
            <input v-model="formModel.code" readonly>
          </label>
          <p v-else class="form-code-hint">表单编码将在保存时由系统自动生成，无需填写。</p>
          <label class="field">
            <span>表单名称：</span>
            <input v-model="formModel.name" placeholder="如 入会申请表">
            <small v-if="formErrors.name" class="err-under">{{ formErrors.name }}</small>
          </label>
          <label class="field">
            <span>版本号：</span>
            <input v-model.number="formModel.version" type="number" placeholder="如 1">
            <small v-if="formErrors.version" class="err-under">{{ formErrors.version }}</small>
          </label>
          <label v-if="!formEditId" class="field">
            <span>复制字段来源：</span>
            <FancySelect
              v-model="cloneSourceFormId"
              icon="⎘"
              :options="[{ value: '', label: '不复制（空白新建）' }, ...forms.map(f => ({ value: String(f.id), label: `${f.name}（${f.code}）` }))]"
              placeholder="选择来源表单"
              :searchable="true"
            />
          </label>
          <label class="field">
            <span>状态：</span>
            <FancySelect v-model="formModel.statusCode" icon="●" :options="statusOptions" placeholder="选择状态" />
          </label>
          <label class="field"><span>备注：</span><input v-model="formModel.remark" placeholder="可空"></label>
          <label class="field full"><span>描述：</span><textarea v-model="formModel.description" placeholder="表单说明"/></label>
        </div>
        </div>
        <div class="dialog-foot"><button class="btn btn-cancel" @click="showFormDialog=false">取消</button><button class="btn btn-primary" @click="submitForm">保存</button></div>
      </div>
    </div>

    <div v-if="showFieldDialog" class="mask" @click.self="showFieldDialog = false">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ fieldEditId ? '编辑字段' : '新增字段' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showFieldDialog = false">×</button>
        </div>
        <div ref="fieldDialogBody" class="dialog-body">
        <div class="form-grid">
          <label class="field full">
            <span>字段名称：</span>
            <input v-model="fieldModel.label" placeholder="如 单位名称">
            <small class="field-hint">字段标识 key 由系统自动生成，无需管理员填写。</small>
            <small v-if="fieldErrors.label" class="err-under">{{ fieldErrors.label }}</small>
          </label>
          <label class="field">
            <span>字段类型：</span>
            <FancySelect v-model="fieldModel.fieldType" icon="▦" :options="fieldTypeOptions" placeholder="选择字段类型" :searchable="true" />
            <small v-if="fieldErrors.fieldType" class="err-under">{{ fieldErrors.fieldType }}</small>
          </label>
          <label v-if="isNumberFieldType(fieldModel.fieldType)" class="field">
            <span>数值单位：</span>
            <FancySelect
              v-model="fieldNumberUnit"
              icon="📐"
              :options="numberUnitOptions"
              placeholder="选择单位（可空）"
              :searchable="true"
            />
            <small class="field-hint">如万元、㎡、人；人脉地图筛选与入会填写时会显示该单位。</small>
          </label>
          <label class="field">
            <span>是否必填：</span>
            <FancySelect v-model="fieldModel.requiredField" icon="!" :options="boolOptions" placeholder="选择是否必填" />
          </label>
          <label class="field">
            <span>所属分组标题：</span>
            <FancySelect
              v-model="fieldUiGroup"
              icon="☰"
              :options="groupSelectOptions"
              placeholder="请选择分组（可空）"
              :searchable="true"
            />
            <small class="field-hint">从分组列表下拉选择；留空则归入“其他信息”。</small>
          </label>
          <label class="field">
            <span>提示：</span>
            <small class="field-hint">
              若要大类-小类级联效果，请在“字段类型”选择级联多选；此处不再提供重复开关。序号由列表顺序自动维护。
            </small>
          </label>
          <label class="field">
            <span>状态：</span>
            <FancySelect v-model="fieldModel.statusCode" icon="●" :options="statusOptions" placeholder="选择状态" />
          </label>
          <label class="field"><span>备注：</span><input v-model="fieldModel.remark" placeholder="可空"></label>
          <label v-if="isOptionFieldType(fieldModel.fieldType)" class="field full">
            <span>选项列表：</span>
            <div v-if="String(fieldUiMode || '').toUpperCase() === 'CASCADE_MULTI'" class="cascade-editor">
              <div class="cascade-editor-toolbar">
                <button class="btn btn-primary" type="button" @click="addCascadeGroup">新增父类</button>
              </div>
              <div v-for="(grp, gIdx) in cascadeGroups" :key="`cascade-grp-${gIdx}`" class="cascade-group" :data-cascade-group-idx="gIdx">
                <div class="cascade-group-head">
                  <div class="cascade-group-main">
                    <label class="cascade-parent">
                      <span>父类</span>
                      <input v-model.trim="grp.parent" placeholder="如：制造业">
                    </label>
                    <label class="cascade-desc">
                      <span>提示说明（选填）</span>
                      <input v-model.trim="grp.desc" placeholder="如：银行|证券|保险|基金|股权投资，前台父类下方灰色小字">
                    </label>
                  </div>
                  <div class="cascade-group-actions">
                    <button class="btn btn-edit" type="button" :disabled="gIdx === 0" @click="moveCascadeGroup(gIdx, -1)">上移</button>
                    <button class="btn btn-edit" type="button" :disabled="gIdx === cascadeGroups.length - 1" @click="moveCascadeGroup(gIdx, 1)">下移</button>
                    <button class="btn btn-delete" type="button" @click="removeCascadeGroup(gIdx)">删除父类</button>
                  </div>
                </div>
                <div class="cascade-child-block">
                  <div class="cascade-child-head">
                    <span>子类</span>
                    <span class="cascade-child-actions">操作</span>
                  </div>
                  <div v-for="(child, cIdx) in grp.children" :key="`cascade-grp-${gIdx}-ch-${cIdx}`" class="cascade-child-row">
                    <input v-model.trim="grp.children[cIdx]" placeholder="如：检测服务">
                    <div class="cascade-child-actions">
                      <button class="btn btn-edit" type="button" :disabled="cIdx === 0" @click="moveCascadeChild(gIdx, cIdx, -1)">上移</button>
                      <button class="btn btn-edit" type="button" :disabled="cIdx === grp.children.length - 1" @click="moveCascadeChild(gIdx, cIdx, 1)">下移</button>
                      <button class="btn btn-delete" type="button" @click="removeCascadeChild(gIdx, cIdx)">删</button>
                    </div>
                  </div>
                  <button class="btn btn-edit" type="button" @click="addCascadeChild(gIdx)">新增子类</button>
                </div>
              </div>
              <div class="cascade-editor-toolbar cascade-editor-toolbar--bottom">
                <button class="btn btn-primary" type="button" @click="addCascadeGroup">新增父类</button>
              </div>
            </div>
            <template v-else>
              <textarea
                v-model="fieldOptionsText"
                :placeholder="fieldOptionsPlaceholder()"
              />
            </template>
            <small class="field-hint">{{ fieldOptionsHintText() }}</small>
          </label>
          <div class="field full">
            <button type="button" class="btn btn-edit btn-sm" @click="showFieldAdvanced = !showFieldAdvanced">
              {{ showFieldAdvanced ? '收起高级配置' : '展开高级配置' }}
            </button>
          </div>
          <label v-if="showFieldAdvanced" class="field full">
            <span>校验JSON（高级，可空）</span>
            <textarea v-model="fieldModel.validationJson" placeholder='如 {"maxLength":50}'/>
            <small class="field-hint">不懂就留空；仅在需要额外校验时使用。</small>
          </label>
        </div>
        </div>
        <div class="dialog-foot"><button class="btn btn-cancel" @click="showFieldDialog=false">取消</button><button class="btn btn-primary" @click="submitField">保存</button></div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'
import {
  clearFormSealImage,
  createForm,
  createFormField,
  createFormGroup,
  deleteForm,
  deleteFormField,
  deleteFormGroup,
  getFormFields,
  getFormGroups,
  getForms,
  reorderFormGroups,
  updateForm,
  updateFormField,
  updateFormGroup,
  uploadFormSealImage,
  uploadFormWordTemplate
} from '@/api'
import { hasAnyPermission } from '@/utils/adminPermission'

const emptyForm = () => ({ code: '', name: '', description: '', statusCode: 'ENABLED', version: 1, remark: '' })
const emptyField = () => ({
  fieldKey: '', label: '', fieldType: 'TEXT', requiredField: false, optionsJson: '', sortOrder: 0,
  validationJson: '', statusCode: 'ENABLED', remark: ''
})

function safeParseOptionsJson(raw) {
  const s = String(raw || '').trim()
  if (!s) return []
  try {
    const arr = JSON.parse(s)
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

function optionsJsonToLines(raw) {
  const arr = safeParseOptionsJson(raw)
  return arr
    .map((it) => {
      if (it && typeof it === 'object') {
        const label = String(it.label || '').trim()
        const value = String(it.value != null ? it.value : '').trim()
        if (!label && !value) return ''
        if (!value || value === label) return label || value
        return `${label}|${value}`
      }
      const text = String(it || '').trim()
      return text
    })
    .filter(Boolean)
    .join('\n')
}

function parseValidationJson(raw) {
  const s = String(raw || '').trim()
  if (!s) return {}
  try {
    const o = JSON.parse(s)
    return o && typeof o === 'object' ? o : {}
  } catch {
    return {}
  }
}

function buildValidationJsonWithUnit(existingJson, unit) {
  const base = parseValidationJson(existingJson)
  const u = String(unit || '').trim()
  if (u) base.unit = u
  else delete base.unit
  const keys = Object.keys(base)
  return keys.length ? JSON.stringify(base) : ''
}

function linesToOptionsJson(linesText) {
  const lines = String(linesText || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  const list = lines.map((line) => {
    const idx = line.indexOf('|')
    if (idx < 0) return { label: line, value: line }
    const label = line.slice(0, idx).trim()
    const value = line.slice(idx + 1).trim() || label
    return { label, value }
  }).filter((o) => String(o.label || '').trim())
  return JSON.stringify(list)
}

export default {
  name: 'FormManageView',
  components: { FancySelect, ListPager },
  props: {
    fixedFormCode: { type: String, default: '' },
    fixedFormName: { type: String, default: '' },
    fixedFormDescription: { type: String, default: '' },
    fixedInitButtonText: { type: String, default: '' },
    showExportConfig: { type: Boolean, default: true },
    formCategory: { type: String, default: '' }
  },
  data() {
    return {
      forms: [], fields: [], currentForm: null, errorMsg: '', okMsg: '',
      formsPage: 1,
      formsPageSize: 10,
      fieldsPage: 1,
      fieldsPageSize: 10,
      showFormDialog: false, formEditId: null, formModel: emptyForm(),
      showFieldDialog: false, fieldEditId: null, fieldModel: emptyField(),
      fieldOptionsText: '',
      fieldNumberUnit: '',
      fieldUiGroup: '',
      fieldUiMode: 'DEFAULT',
      showFieldAdvanced: false,
      /** 级联多选：按父类分组维护子类，避免重复输入父类 */
      cascadeGroups: [],
      groupCatalog: [],
      groupDraftTitle: '',
      groupRenameIdx: -1,
      groupRenameDraft: '',
      selectedFieldIds: [],
      bulkGroupTitle: '',
      bulkBusy: false,
      sortBusy: false,
      dragFieldId: null,
      dragFromIndex: -1,
      fieldSearchKeyword: '',
      fieldFilterType: '',
      fieldFilterGroup: '',
      fieldFilterRequired: '',
      formErrors: {},
      fieldErrors: {},
      cloneSourceFormId: '',
      fixedFormMissing: false,
      fixedInitBusy: false,
      canFormEdit: false,
      canFormDelete: false,
      statusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' }
      ],
      boolOptions: [
        { value: true, label: '是' },
        { value: false, label: '否' }
      ],
      fieldTypeOptions: [
        { value: 'TEXT', label: '单行文本(TEXT)' },
        { value: 'NUMBER', label: '数字(NUMBER)' },
        { value: 'TEXTAREA', label: '多行文本(TEXTAREA)' },
        { value: 'TAGS', label: '标签列表(TAGS)' },
        { value: 'RADIO', label: '单选(RADIO)' },
        { value: 'CHECKBOX', label: '多选(CHECKBOX)' },
        { value: 'CHECKBOX_GROUP', label: '多选(CHECKBOX_GROUP)' },
        { value: 'SELECT', label: '下拉(SELECT)' },
        { value: 'DATE', label: '日期(DATE)' },
        { value: 'DATETIME', label: '日期时间(DATETIME)' },
        { value: 'IMAGE', label: '图片(IMAGE)' },
        { value: 'CASCADER_MULTI', label: '级联多选(CASCADER_MULTI)' }
      ],
      uiModeOptions: [
        { value: 'DEFAULT', label: '默认（普通）' },
        { value: 'CASCADE_MULTI', label: '行业级联多选弹窗' }
      ],
      numberUnitOptions: [
        { value: '', label: '无单位' },
        { value: '元', label: '元（货币）' },
        { value: '万元', label: '万元（货币）' },
        { value: '㎡', label: '㎡（面积）' },
        { value: 'm²', label: 'm²（面积）' },
        { value: '亩', label: '亩（面积）' },
        { value: '个', label: '个（个数）' },
        { value: '项', label: '项（个数）' },
        { value: '件', label: '件（个数）' },
        { value: '台', label: '台（个数）' },
        { value: '套', label: '套（个数）' },
        { value: '人', label: '人（人数）' },
        { value: '名', label: '名（人数）' },
        { value: '吨', label: '吨（质量）' },
        { value: 'kg', label: 'kg（质量）' },
        { value: 'g', label: 'g（质量）' },
        { value: 'L', label: 'L（体积）' },
        { value: 'm³', label: 'm³（体积）' },
        { value: 'm', label: 'm（长度）' },
        { value: 'km', label: 'km（长度）' },
        { value: 'cm', label: 'cm（长度）' },
        { value: 'mm', label: 'mm（长度）' },
        { value: '%', label: '%（比例）' },
        { value: '年', label: '年（时间）' },
        { value: '月', label: '月（时间）' },
        { value: '天', label: '天（时间）' },
        { value: '小时', label: '小时（时间）' }
      ]
    }
  },
  computed: {
    isFixedFormMode() {
      return !!String(this.fixedFormCode || '').trim()
    },
    displayForms() {
      return (this.forms || []).slice().sort((a, b) => Number(a.id) - Number(b.id))
    },
    formsRowOffset() {
      return pageRowOffset(this.formsPage, this.formsPageSize)
    },
    pagedDisplayForms() {
      return slicePageRows(this.displayForms, this.formsPage, this.formsPageSize)
    },
    groupSelectOptions() {
      const list = (this.groupCatalog || []).map((g) => ({ value: String(g.id), label: g.title }))
      return [{ value: '', label: '不归属分组（默认“其他信息”）' }, ...list]
    },
    fieldFilterTypeOptions() {
      return [{ value: '', label: '全部类型' }, ...(this.fieldTypeOptions || [])]
    },
    fieldFilterGroupOptions() {
      const list = (this.groupCatalog || []).map((g) => ({ value: String(g.id), label: g.title }))
      return [{ value: '', label: '全部分组' }, { value: '__ungrouped__', label: '未分组（其他信息）' }, ...list]
    },
    fieldFilterRequiredOptions() {
      return [
        { value: '', label: '全部' },
        { value: '1', label: '必填' },
        { value: '0', label: '非必填' }
      ]
    },
    selectedFieldCount() {
      return Array.isArray(this.selectedFieldIds) ? this.selectedFieldIds.length : 0
    },
    allFieldsSelected() {
      const list = this.filteredFields || []
      if (!list.length) return false
      const selected = new Set((this.selectedFieldIds || []).map((id) => Number(id)))
      return list.every((f) => selected.has(Number(f.id)))
    },
    filteredFields() {
      const kw = String(this.fieldSearchKeyword || '').trim().toLowerCase()
      const list = Array.isArray(this.fields) ? this.fields : []
      const typeFilter = String(this.fieldFilterType || '').trim().toUpperCase()
      const groupFilter = String(this.fieldFilterGroup || '').trim()
      const requiredFilter = String(this.fieldFilterRequired || '').trim()
      return list.filter((f) => {
        const label = String(f?.label || '').toLowerCase()
        const key = String(f?.fieldKey || '').toLowerCase()
        const type = String(f?.fieldType || '').toUpperCase()
        const groupName = String(this.groupNameOfField(f) || '')
        const group = groupName.toLowerCase()
        const textHit = !kw || label.includes(kw) || key.includes(kw) || type.toLowerCase().includes(kw) || group.includes(kw)
        const typeHit = !typeFilter || type === typeFilter
        const groupId = f && f.groupId != null ? String(f.groupId) : ''
        const groupHit = !groupFilter || (groupFilter === '__ungrouped__' ? !groupId : groupId === groupFilter)
        const requiredHit = !requiredFilter || (requiredFilter === '1' ? !!f.requiredField : !f.requiredField)
        return textHit && typeHit && groupHit && requiredHit
      })
    },
    fieldsRowOffset() {
      return pageRowOffset(this.fieldsPage, this.fieldsPageSize)
    },
    pagedFilteredFields() {
      return slicePageRows(this.filteredFields, this.fieldsPage, this.fieldsPageSize)
    },
    canAddGroupItem() {
      return !!this.canFormEdit && !!String(this.groupDraftTitle || '').trim()
    }
  },
  watch: {
    'displayForms.length'(n) {
      this.formsPage = clampPage(this.formsPage, n, this.formsPageSize)
    },
    'filteredFields.length'(n) {
      this.fieldsPage = clampPage(this.fieldsPage, n, this.fieldsPageSize)
    },
    'fieldModel.fieldType'(val) {
      const t = String(val || '').toUpperCase()
      if (t === 'CASCADER_MULTI') {
        this.fieldUiMode = 'CASCADE_MULTI'
        if (!Array.isArray(this.cascadeGroups) || !this.cascadeGroups.length) {
          this.cascadeGroups = [{ parent: '', desc: '', children: [''] }]
        }
      }
    },
  },
  mounted() {
    this.canFormEdit = hasAnyPermission(['form.manage', 'form.edit'])
    this.canFormDelete = hasAnyPermission(['form.manage', 'form.delete'])
    this.loadForms()
  },
  methods: {
    fixedNameDefault() {
      const fromProp = String(this.fixedFormName || '').trim()
      if (fromProp) return fromProp
      const code = String(this.fixedFormCode || '').trim().toLowerCase()
      if (code === 'activity_signup') return '活动报名表'
      if (code === 'membership_apply') return '入会申请表'
      if (code === 'visit_register') return '来访登记'
      if (code === 'walk_register') return '走访登记'
      if (code === 'member_service') return '会员服务'
      return '固定表单'
    },
    fixedDescDefault() {
      const fromProp = String(this.fixedFormDescription || '').trim()
      if (fromProp) return fromProp
      const code = String(this.fixedFormCode || '').trim().toLowerCase()
      if (code === 'activity_signup') return '活动报名固定表单'
      if (code === 'membership_apply') return '入会申请固定表单'
      if (code === 'visit_register') return '服务表单-来访登记'
      if (code === 'walk_register') return '服务表单-走访登记'
      if (code === 'member_service') return '服务表单-会员服务'
      return '固定表单'
    },
    fixedInitBtnTextDefault() {
      const fromProp = String(this.fixedInitButtonText || '').trim()
      if (fromProp) return fromProp
      return `一键初始化${this.fixedNameDefault()}`
    },
    normOptionValue(text) {
      return String(text || '')
        .trim()
        .toLowerCase()
        .replace(/[\s\-\\/]+/g, '_')
        .replace(/[^\w\u4e00-\u9fa5]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
    },
    addCascadeGroup() {
      this.cascadeGroups.push({ parent: '', desc: '', children: [''] })
      this.scrollToCascadeGroup(this.cascadeGroups.length - 1)
    },
    scrollToCascadeGroup(idx) {
      this.$nextTick(() => {
        const body = this.$refs.fieldDialogBody
        if (!body) return
        const el = body.querySelector(`[data-cascade-group-idx="${idx}"]`)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        const input = el.querySelector('.cascade-parent input')
        if (input && typeof input.focus === 'function') {
          try {
            input.focus({ preventScroll: true })
          } catch {
            input.focus()
          }
        }
      })
    },
    removeCascadeGroup(idx) {
      if (idx < 0 || idx >= this.cascadeGroups.length) return
      this.cascadeGroups.splice(idx, 1)
      if (!this.cascadeGroups.length) this.addCascadeGroup()
    },
    addCascadeChild(gIdx) {
      const g = this.cascadeGroups[gIdx]
      if (!g) return
      if (!Array.isArray(g.children)) g.children = []
      g.children.push('')
    },
    removeCascadeChild(gIdx, cIdx) {
      const g = this.cascadeGroups[gIdx]
      if (!g || !Array.isArray(g.children)) return
      g.children.splice(cIdx, 1)
      if (!g.children.length) g.children = ['']
    },
    moveCascadeGroup(gIdx, delta) {
      const to = gIdx + delta
      if (to < 0 || to >= this.cascadeGroups.length) return
      const arr = [...this.cascadeGroups]
      const temp = arr[gIdx]
      arr[gIdx] = arr[to]
      arr[to] = temp
      this.cascadeGroups = arr
    },
    moveCascadeChild(gIdx, cIdx, delta) {
      const g = this.cascadeGroups[gIdx]
      if (!g || !Array.isArray(g.children)) return
      const to = cIdx + delta
      if (to < 0 || to >= g.children.length) return
      const children = g.children.slice()
      const temp = children[cIdx]
      children[cIdx] = children[to]
      children[to] = temp
      this.cascadeGroups = this.cascadeGroups.map((item, i) => (i === gIdx ? { ...item, children } : item))
    },
    cascadeGroupsFromJson(raw) {
      const s = String(raw || '').trim()
      if (!s) return [{ parent: '', desc: '', children: [''] }]
      try {
        const arr = JSON.parse(s)
        if (!Array.isArray(arr)) return [{ parent: '', desc: '', children: [''] }]
        const groups = []
        arr.forEach((cat) => {
          const p = String(cat?.label || '').trim()
          const desc = String(cat?.desc || '').trim()
          const children = Array.isArray(cat?.children) ? cat.children : []
          const childLabels = children
            .map((c) => String(c?.label || '').trim())
            .filter(Boolean)
          groups.push({ parent: p, desc, children: childLabels.length ? childLabels : [''] })
        })
        return groups.length ? groups : [{ parent: '', desc: '', children: [''] }]
      } catch {
        return [{ parent: '', desc: '', children: [''] }]
      }
    },
    cascadeJsonFromGroups(groups) {
      const result = (groups || [])
        .map((g) => {
          const parent = String(g?.parent || '').trim()
          const desc = String(g?.desc || '').trim()
          const children = Array.isArray(g?.children) ? g.children : []
          const uniq = []
          const seen = new Set()
          children.forEach((c) => {
            const t = String(c || '').trim()
            if (!t) return
            if (seen.has(t)) return
            seen.add(t)
            uniq.push(t)
          })
          if (!parent || !uniq.length) return null
          const node = {
            label: parent,
            value: this.normOptionValue(parent) || parent,
            children: uniq.map((child) => ({
              label: child,
              value: this.normOptionValue(child) || child,
            })),
          }
          if (desc) node.desc = desc
          return node
        })
        .filter(Boolean)
      return JSON.stringify(result)
    },
    groupNameOfField(f) {
      const gid = Number(f && f.groupId ? f.groupId : 0)
      if (!gid) return '其他信息'
      const hit = (this.groupCatalog || []).find((g) => Number(g.id) === gid)
      return hit && hit.title ? hit.title : '其他信息'
    },
    onDragStartField(e, f, idx) {
      if (!this.canFormEdit || this.sortBusy) return
      this.dragFieldId = f && f.id ? f.id : null
      this.dragFromIndex = Number.isFinite(idx) ? idx : -1
      try {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(this.dragFieldId || ''))
      } catch {
        // ignore
      }
    },
    onDragOverField() {
      // 只用于触发 drop
    },
    async onDropField(_e, _f, dropIdx) {
      if (!this.canFormEdit || this.sortBusy) return
      const from = Number(this.dragFromIndex)
      const to = Number(dropIdx)
      if (!Number.isFinite(from) || !Number.isFinite(to)) return
      if (from < 0 || to < 0 || from === to) return
      const arr = [...(this.fields || [])]
      if (from >= arr.length || to >= arr.length) return
      const moved = arr.splice(from, 1)[0]
      arr.splice(to, 0, moved)
      this.fields = arr
      this.dragFromIndex = to
      // 拖拽时不强制 reload，避免闪烁
      await this.reindexSortOrder({ reloadAfter: false })
    },
    onDragEndField() {
      this.dragFieldId = null
      this.dragFromIndex = -1
    },
    isLegacySortByTens(list) {
      const arr = Array.isArray(list) ? list : []
      if (!arr.length) return false
      const nums = arr.map((x) => Number(x && x.sortOrder != null ? x.sortOrder : 0))
      if (nums.some((n) => !Number.isFinite(n))) return false
      // 常见旧值：10,20,30... 或 0,10,20...
      const tensLike = nums.every((n) => n % 10 === 0)
      if (!tensLike) return false
      const max = Math.max(...nums)
      return max >= 10
    },
    async reindexSortOrder(opts = {}) {
      const reloadAfter = opts.reloadAfter !== false
      if (!this.currentForm || !this.fields.length) return
      this.sortBusy = true
      try {
        // 按当前列表顺序，重新写 sortOrder = 1,2,3...
        for (let i = 0; i < this.fields.length; i += 1) {
          const row = this.fields[i]
          const nextSort = i + 1
          if (Number(row.sortOrder || 0) === nextSort) continue
          row.sortOrder = nextSort
          await updateFormField(row.id, {
            fieldKey: row.fieldKey,
            label: row.label,
            fieldType: row.fieldType,
            groupId: row.groupId || null,
            uiMode: row.uiMode || null,
            requiredField: !!row.requiredField,
            optionsJson: row.optionsJson || '',
            sortOrder: nextSort,
            validationJson: row.validationJson || '',
            statusCode: row.statusCode || 'ENABLED',
            remark: row.remark || ''
          })
        }
        if (reloadAfter) await this.loadFields(this.currentForm)
      } catch (e) {
        this.errorMsg = e.message || '重排排序值失败'
      } finally {
        this.sortBusy = false
      }
    },
    validateFormModel() {
      const e = {}
      const code = String(this.formModel.code || '').trim()
      const name = String(this.formModel.name || '').trim()
      const version = Number(this.formModel.version)
      if (this.formEditId && !code) e.code = '表单编码不能为空'
      if (!name) e.name = '请填写表单名称'
      if (!Number.isFinite(version) || version <= 0) e.version = '版本号必须是大于 0 的数字'
      this.formErrors = e
      return Object.keys(e).length === 0
    },
    validateFieldModel() {
      const e = {}
      const label = String(this.fieldModel.label || '').trim()
      const fieldType = String(this.fieldModel.fieldType || '').trim()
      if (!label) e.label = '请填写字段名称'
      if (!fieldType) e.fieldType = '请选择字段类型'
      this.fieldErrors = e
      return Object.keys(e).length === 0
    },
    async moveField(idx, delta) {
      const to = idx + delta
      if (to < 0 || to >= this.fields.length) return
      const arr = [...this.fields]
      const tmp = arr[idx]
      arr[idx] = arr[to]
      arr[to] = tmp
      this.fields = arr
      await this.reindexSortOrder()
    },
    normalizeGroupCode(text) {
      return String(text || '')
        .trim()
        .toLowerCase()
        .replace(/[\s\-\\/]+/g, '_')
        .replace(/[^a-z0-9_\u4e00-\u9fa5]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
    },
    reorderGroupCatalog() {
      this.groupCatalog = (this.groupCatalog || [])
        .map((g, idx) => ({
          ...g,
          order: idx + 1
        }))
    },
    rebuildGroupCatalogFromFields() {
      const map = {}
      ;(this.groupCatalog || []).forEach((g, idx) => {
        const code = this.normalizeGroupCode(g.code || g.title)
        if (!code) return
        map[code] = {
          id: g.id ?? null,
          code,
          title: String(g.title || '').trim(),
          order: Number(g.order || idx + 1)
        }
      })
      ;(this.fields || []).forEach((f) => {
        const gid = Number(f.groupId || 0)
        if (!gid) return
        const hit = (this.groupCatalog || []).find((g) => Number(g.id) === gid)
        const title = String((hit && hit.title) || '').trim()
        if (!title) return
        const code = this.normalizeGroupCode((hit && hit.code) || title)
        const order = Number((hit && hit.order) || 9999)
        const old = map[code]
        if (!old) map[code] = { id: hit && hit.id ? hit.id : null, code, title, order }
        else {
          old.id = old.id || (hit && hit.id ? hit.id : null)
          old.title = old.title || title
          old.order = Math.min(Number(old.order || 9999), order)
        }
      })
      this.groupCatalog = Object.values(map)
        .sort((a, b) => Number(a.order || 9999) - Number(b.order || 9999))
        .map((g, idx) => ({ ...g, order: idx + 1 }))
    },
    addGroupItem() {
      this.errorMsg = ''
      this.okMsg = ''
      if (!this.canFormEdit) {
        this.errorMsg = '当前账号没有表单编辑权限，无法添加分组'
        return
      }
      if (!this.currentForm || !this.currentForm.id) {
        this.errorMsg = '请先选择一个表单，再添加分组'
        return
      }
      const title = String(this.groupDraftTitle || '').trim()
      if (!title) {
        this.errorMsg = '请先填写分组标题，再点“添加分组”'
        return
      }
      const code = this.normalizeGroupCode(title)
      if (!code) return
      const exists = (this.groupCatalog || []).some((g) => this.normalizeGroupCode(g.code || g.title) === code)
      if (exists) {
        this.errorMsg = '该分组标题已存在，请更换后再添加'
        return
      }
      const doCreate = async () => {
        try {
          const saved = await createFormGroup(this.currentForm.id, {
            groupCode: code,
            groupName: title,
            sortOrder: this.groupCatalog.length + 1,
            statusCode: 'ENABLED',
            remark: ''
          })
          this.groupCatalog.push({
            id: saved.id,
            code: saved.groupCode,
            title: saved.groupName,
            order: Number(saved.sortOrder || this.groupCatalog.length + 1)
          })
          this.groupDraftTitle = ''
          this.reorderGroupCatalog()
          this.okMsg = '分组已添加'
        } catch (e) {
          this.errorMsg = e.message || '新增分组失败'
        }
      }
      doCreate()
    },
    moveGroupItem(idx, delta) {
      if (this.groupRenameIdx >= 0) this.cancelRenameGroupItem()
      const to = idx + delta
      if (to < 0 || to >= this.groupCatalog.length) return
      const arr = [...this.groupCatalog]
      const temp = arr[idx]
      arr[idx] = arr[to]
      arr[to] = temp
      this.groupCatalog = arr
      this.reorderGroupCatalog()
    },
    startRenameGroupItem(idx) {
      this.errorMsg = ''
      this.okMsg = ''
      if (!this.canFormEdit) {
        this.errorMsg = '当前账号没有表单编辑权限，无法重命名分组'
        return
      }
      const row = this.groupCatalog[idx]
      if (!row) return
      this.groupRenameIdx = idx
      this.groupRenameDraft = String(row.title || '').trim()
    },
    cancelRenameGroupItem() {
      this.groupRenameIdx = -1
      this.groupRenameDraft = ''
    },
    async confirmRenameGroupItem() {
      this.errorMsg = ''
      this.okMsg = ''
      if (!this.canFormEdit) {
        this.errorMsg = '当前账号没有表单编辑权限，无法重命名分组'
        return
      }
      const idx = this.groupRenameIdx
      const row = idx >= 0 ? this.groupCatalog[idx] : null
      if (!row) {
        this.cancelRenameGroupItem()
        return
      }
      const title = String(this.groupRenameDraft || '').trim()
      if (!title) {
        this.errorMsg = '分组标题不能为空'
        return
      }
      if (title === String(row.title || '').trim()) {
        this.cancelRenameGroupItem()
        return
      }
      const dup = (this.groupCatalog || []).some(
        (g, i) => i !== idx && String(g.title || '').trim() === title,
      )
      if (dup) {
        this.errorMsg = '该分组标题已存在，请更换后再保存'
        return
      }
      try {
        // 只改显示名，保留原 groupCode（尤其是默认 other_info），字段仍按 groupId 绑定
        if (row.id) {
          const saved = await updateFormGroup(row.id, {
            groupCode: row.code,
            groupName: title,
            sortOrder: Number(row.order) || idx + 1,
            statusCode: 'ENABLED',
            remark: '',
          })
          row.title = saved.groupName || title
          if (saved.groupCode) row.code = saved.groupCode
          if (saved.sortOrder != null) row.order = Number(saved.sortOrder)
        } else {
          row.title = title
        }
        this.cancelRenameGroupItem()
        this.okMsg = '分组标题已更新'
        if (this.currentForm) await this.loadFields(this.currentForm)
      } catch (e) {
        this.errorMsg = e.message || '重命名分组失败'
      }
    },
    removeGroupItem(idx) {
      if (this.groupRenameIdx >= 0) this.cancelRenameGroupItem()
      const row = this.groupCatalog[idx]
      if (row && row.code === 'other_info') {
        this.errorMsg = '默认分组不可删除（可「重命名」修改显示标题）'
        return
      }
      if (!row) return
      const doRemove = async () => {
        try {
          if (row.id) await deleteFormGroup(row.id)
          this.groupCatalog.splice(idx, 1)
          this.reorderGroupCatalog()
          if (this.currentForm) await this.loadFields(this.currentForm)
        } catch (e) {
          this.errorMsg = e.message || '删除分组失败'
        }
      }
      doRemove()
    },
    async persistGroupCatalog() {
      if (!this.currentForm || !this.groupCatalog.length) return
      try {
        const ids = []
        for (let i = 0; i < this.groupCatalog.length; i += 1) {
          const g = this.groupCatalog[i]
          const payload = {
            groupCode: g.code,
            groupName: g.title,
            sortOrder: i + 1,
            statusCode: 'ENABLED',
            remark: ''
          }
          if (g.id) {
            const saved = await updateFormGroup(g.id, payload)
            ids.push(saved.id)
          } else {
            const saved = await createFormGroup(this.currentForm.id, payload)
            ids.push(saved.id)
          }
        }
        await reorderFormGroups(this.currentForm.id, ids)
        await this.loadFields(this.currentForm)
        this.errorMsg = ''
      } catch (e) {
        this.errorMsg = e.message || '保存分组顺序失败'
      }
    },
    ensureFieldGroupInCatalog(groupTitle) {
      const title = String(groupTitle || '').trim()
      if (!title) return
      const code = this.normalizeGroupCode(title)
      if (!code) return
      const exists = (this.groupCatalog || []).some((g) => this.normalizeGroupCode(g.code || g.title) === code)
      if (exists) return
      this.groupCatalog.push({ id: null, code, title, order: this.groupCatalog.length + 1 })
      this.reorderGroupCatalog()
    },
    groupMetaByTitle(groupTitle) {
      const title = String(groupTitle || '').trim()
      if (!title) return null
      const code = this.normalizeGroupCode(title)
      const hit = (this.groupCatalog || []).find((g) => this.normalizeGroupCode(g.code || g.title) === code)
      if (!hit) return null
      return { id: hit.id, code: hit.code, title: hit.title, order: Number(hit.order || 9999) }
    },
    async loadForms() {
      try {
        const cat = String(this.formCategory || '').trim()
        const rows = (await getForms(cat ? { category: cat } : undefined)) || []
        this.forms = rows.slice().sort((a, b) => Number(a.id) - Number(b.id))
        this.tryAutoOpenFromQuery()
      } catch (e) { this.errorMsg = e.message || '读取失败' }
    },
    tryAutoOpenFromQuery() {
      const fixed = String(this.fixedFormCode || '').trim()
      const code = fixed || String(this.$route?.query?.code || '').trim()
      if (!code) return
      const found = (this.forms || []).find((f) => String(f.code || '').trim() === code)
      if (found) {
        this.fixedFormMissing = false
        this.loadFields(found)
      } else if (fixed) {
        this.fixedFormMissing = true
      }
    },
    async initFixedForm() {
      const code = String(this.fixedFormCode || '').trim()
      if (!code) return
      this.fixedInitBusy = true
      this.errorMsg = ''
      try {
        await createForm({
          code,
          name: this.fixedNameDefault(),
          description: this.fixedDescDefault(),
          statusCode: 'ENABLED',
          version: 1,
          remark: `由表单管理一键初始化：${this.fixedNameDefault()}`
        })
        await this.loadForms()
      } catch (e) {
        this.errorMsg = e.message || '初始化表单失败'
      } finally {
        this.fixedInitBusy = false
      }
    },
    formUpsertPayload() {
      const m = this.formModel || {}
      const cat = String(this.formCategory || '').trim()
      const code = String(m.code || '').trim()
      return {
        code: this.formEditId ? code : (code || ''),
        name: m.name,
        description: m.description || '',
        statusCode: m.statusCode,
        version: m.version,
        remark: m.remark || '',
        ...(cat ? { formCategory: cat } : {}),
      }
    },
    syncCurrentFormFromList() {
      if (!this.currentForm) return
      const row = (this.forms || []).find((x) => Number(x.id) === Number(this.currentForm.id))
      if (row) this.currentForm = { ...row }
    },
    async onWordTemplateFile(e) {
      const input = e.target
      const file = input.files && input.files[0]
      if (!file || !this.currentForm) return
      try {
        await uploadFormWordTemplate(this.currentForm.id, file)
        await this.loadForms()
        this.syncCurrentFormFromList()
      } catch (err) {
        this.errorMsg = err.message || '模板上传失败'
      } finally {
        input.value = ''
      }
    },
    async onSealImageFile(e) {
      const input = e.target
      const file = input.files && input.files[0]
      if (!file || !this.currentForm) return
      try {
        await uploadFormSealImage(this.currentForm.id, file)
        await this.loadForms()
        this.syncCurrentFormFromList()
      } catch (err) {
        this.errorMsg = err.message || '印章上传失败'
      } finally {
        input.value = ''
      }
    },
    async onClearSeal() {
      if (!this.currentForm || !this.currentForm.sealImagePath) return
      if (!await adminConfirm('确定清除已上传的印章图吗？')) return
      try {
        await clearFormSealImage(this.currentForm.id)
        await this.loadForms()
        this.syncCurrentFormFromList()
      } catch (err) {
        this.errorMsg = err.message || '清除失败'
      }
    },
    async loadFields(form) {
      this.fieldsPage = 1
      this.cancelRenameGroupItem()
      const fromList = (this.forms || []).find((x) => Number(x.id) === Number(form.id))
      this.currentForm = { ...(fromList || form) }
      this.fixedFormMissing = false
      try {
        // 分组必须按“当前表单”隔离，切换表单时清空，避免混用
        this.groupCatalog = []
        this.groupCatalog = ((await getFormGroups(form.id)) || []).map((g) => ({
          id: g.id,
          code: g.groupCode,
          title: g.groupName,
          order: Number(g.sortOrder || 9999)
        }))
        this.fields = (await getFormFields(form.id)) || []
        // 自动把历史 10/20/30… 规范成 1/2/3…（用户不需要点任何按钮）
        if (this.isLegacySortByTens(this.fields)) {
          this.fields = this.fields.slice().sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
          await this.reindexSortOrder({ reloadAfter: false })
        }
        this.selectedFieldIds = []
        this.bulkGroupTitle = ''
        this.rebuildGroupCatalogFromFields()
      } catch (e) { this.errorMsg = e.message || '读取字段失败' }
    },
    isFieldSelected(id) {
      const nid = Number(id)
      return (this.selectedFieldIds || []).includes(nid)
    },
    toggleFieldSelection(id) {
      const nid = Number(id)
      const arr = Array.isArray(this.selectedFieldIds) ? [...this.selectedFieldIds] : []
      const idx = arr.indexOf(nid)
      if (idx >= 0) arr.splice(idx, 1)
      else arr.push(nid)
      this.selectedFieldIds = arr
    },
    toggleSelectAllFields() {
      if (this.allFieldsSelected) {
        const visibleIds = new Set(
          (this.filteredFields || []).map((f) => Number(f.id)).filter((x) => Number.isFinite(x))
        )
        this.selectedFieldIds = (this.selectedFieldIds || []).filter((id) => !visibleIds.has(Number(id)))
        return
      }
      const set = new Set((this.selectedFieldIds || []).map((id) => Number(id)).filter((x) => Number.isFinite(x)))
      for (const f of this.filteredFields || []) {
        const nid = Number(f.id)
        if (Number.isFinite(nid)) set.add(nid)
      }
      this.selectedFieldIds = Array.from(set)
    },
    clearFieldSelection() {
      this.selectedFieldIds = []
    },
    resetFieldFilters() {
      this.fieldsPage = 1
      this.fieldSearchKeyword = ''
      this.fieldFilterType = ''
      this.fieldFilterGroup = ''
      this.fieldFilterRequired = ''
    },
    async applyBulkGroup() {
      if (!this.currentForm) return
      const ids = Array.isArray(this.selectedFieldIds) ? [...this.selectedFieldIds] : []
      if (!ids.length) return
      this.bulkBusy = true
      try {
        // 允许选择空分组：表示清空分组归属
        const selectedGroupId = Number(this.bulkGroupTitle || 0)
        const meta = selectedGroupId
          ? ((this.groupCatalog || []).find((g) => Number(g.id) === selectedGroupId) || null)
          : null

        for (let i = 0; i < ids.length; i += 1) {
          const id = ids[i]
          const row = (this.fields || []).find((f) => Number(f.id) === Number(id))
          if (!row) continue
          await updateFormField(row.id, {
            fieldKey: row.fieldKey,
            label: row.label,
            fieldType: row.fieldType,
            groupId: meta ? meta.id : null,
            uiMode: row.uiMode || null,
            requiredField: !!row.requiredField,
            optionsJson: row.optionsJson || '',
            sortOrder: row.sortOrder ?? i * 10,
            validationJson: row.validationJson || '',
            statusCode: row.statusCode || 'ENABLED',
            remark: row.remark || ''
          })
        }
        await this.loadFields(this.currentForm)
      } catch (e) {
        this.errorMsg = e.message || '批量归组失败'
      } finally {
        this.bulkBusy = false
      }
    },
    openCreateForm() {
      this.formEditId = null
      this.formModel = emptyForm()
      this.cloneSourceFormId = ''
      this.showFormDialog = true
    },
    openEditForm(item) {
      this.formEditId = item.id
      this.formModel = { ...emptyForm(), ...item }
      this.cloneSourceFormId = ''
      this.showFormDialog = true
    },
    statusText(code) {
      return code === 'ENABLED' ? '启用' : code === 'DISABLED' ? '停用' : code
    },
    async submitForm() {
      try {
        if (!this.validateFormModel()) return
        const payload = this.formUpsertPayload()
        if (this.formEditId) {
          await updateForm(this.formEditId, payload)
        } else {
          const created = await createForm(payload)
          const srcId = Number(this.cloneSourceFormId || 0)
          if (created?.id && srcId > 0) {
            await this.copyFieldsFromForm(srcId, created.id)
          }
        }
        this.showFormDialog = false
        await this.loadForms()
      } catch (e) { this.errorMsg = e.message || '保存失败' }
    },
    async onDeleteForm(item) {
      if (!await adminConfirm(`确定删除表单 ${item.name} 吗？`)) return
      try { await deleteForm(item.id); await this.loadForms() } catch (e) { this.errorMsg = e.message || '删除失败' }
    },
    isOptionFieldType(type) {
      const t = String(type || '').toUpperCase()
      return t === 'RADIO' || t === 'SELECT' || t === 'CHECKBOX' || t === 'CHECKBOX_GROUP' || t === 'CASCADER_MULTI'
    },
    isNumberFieldType(type) {
      return String(type || '').toUpperCase() === 'NUMBER'
    },
    fieldOptionsPlaceholder() {
      if (String(this.fieldUiMode || '').toUpperCase() === 'CASCADE_MULTI') {
        return '请使用下方「父类分组 + 子类列表」录入，无需手写 JSON'
      }
      return '每行一个；格式：显示文案|值。\n示例：\n普通会员|member\n理事单位|director\n其他'
    },
    fieldOptionsHintText() {
      if (String(this.fieldUiMode || '').toUpperCase() === 'CASCADE_MULTI') {
        return '按父类分组维护子类；「提示说明」为选填，保存后在小程序父类名称下方以灰色小字展示（如：银行|证券|保险）。'
      }
      return '仅单选/下拉/多选使用。若一行不写“|值”，系统默认用文案作为值。'
    },
    openCreateField() {
      const nextSort = ((this.fields || []).reduce((m, f) => Math.max(m, Number(f?.sortOrder || 0)), 0) || 0) + 1
      this.fieldEditId = null
      this.fieldModel = { ...emptyField(), sortOrder: nextSort }
      this.fieldOptionsText = ''
      this.fieldNumberUnit = ''
      this.fieldUiGroup = ''
      this.fieldUiMode = 'DEFAULT'
      this.showFieldAdvanced = false
      this.cascadeGroups = [{ parent: '', desc: '', children: [''] }]
      this.showFieldDialog = true
    },
    openEditField(item) {
      this.fieldEditId = item.id
      this.fieldModel = { ...emptyField(), ...item }
      this.fieldUiGroup = item.groupId ? String(item.groupId) : ''
      this.fieldUiMode = String(item.uiMode || 'DEFAULT').trim() || 'DEFAULT'
      if (String(this.fieldModel.fieldType || '').toUpperCase() === 'CASCADER_MULTI') {
        this.fieldUiMode = 'CASCADE_MULTI'
      }
      if (this.fieldUiMode === 'CASCADE_MULTI') {
        this.cascadeGroups = this.cascadeGroupsFromJson(item.optionsJson)
        this.fieldOptionsText = String(item.optionsJson || '')
      } else {
        this.fieldOptionsText = optionsJsonToLines(item.optionsJson)
        this.cascadeGroups = [{ parent: '', desc: '', children: [''] }]
      }
      const v = parseValidationJson(item.validationJson)
      this.fieldNumberUnit = String(v.unit || v.numberUnit || v.fieldUnit || '').trim()
      this.showFieldAdvanced = false
      this.showFieldDialog = true
    },
    async submitField() {
      if (!this.currentForm) return
      try {
        if (!this.validateFieldModel()) return
        const payload = { ...this.fieldModel }
        if (!this.fieldEditId) {
          const maxSort = ((this.fields || []).reduce((m, f) => Math.max(m, Number(f?.sortOrder || 0)), 0) || 0)
          const currentSort = Number(payload.sortOrder || 0)
          payload.sortOrder = currentSort > 0 ? currentSort : (maxSort + 1)
        }
        payload.fieldKey = payload.fieldKey || this.buildFieldKey(payload.label)
        if (!payload.fieldKey) throw new Error('字段 key 生成失败，请手动填写')
        const gid = Number(this.fieldUiGroup || 0)
        payload.groupId = gid > 0 ? gid : null
        if (String(payload.fieldType || '').toUpperCase() === 'CASCADER_MULTI') {
          this.fieldUiMode = 'CASCADE_MULTI'
        }
        payload.uiMode = this.fieldUiMode && this.fieldUiMode !== 'DEFAULT' ? this.fieldUiMode : null
        if (this.isOptionFieldType(payload.fieldType)) {
          if (String(this.fieldUiMode || '').toUpperCase() === 'CASCADE_MULTI') {
            const raw = this.cascadeJsonFromGroups(this.cascadeGroups)
            let arr = []
            try { arr = JSON.parse(raw || '[]') } catch { arr = [] }
            if (!Array.isArray(arr) || !arr.length) throw new Error('行业级联配置至少需要一个父类')
            payload.optionsJson = JSON.stringify(arr)
          } else {
            payload.optionsJson = linesToOptionsJson(this.fieldOptionsText)
          }
          const options = safeParseOptionsJson(payload.optionsJson)
          if (!options.length) throw new Error('该字段类型需要至少一个选项')
        } else {
          payload.optionsJson = ''
        }
        if (this.isNumberFieldType(payload.fieldType)) {
          payload.validationJson = buildValidationJsonWithUnit(payload.validationJson, this.fieldNumberUnit)
        } else {
          payload.validationJson = payload.validationJson || ''
        }
        if (this.fieldEditId) await updateFormField(this.fieldEditId, payload)
        else await createFormField(this.currentForm.id, payload)
        this.showFieldDialog = false
        await this.loadFields(this.currentForm)
      } catch (e) { this.errorMsg = e.message || '保存字段失败' }
    },
    buildFieldKey(label) {
      if (!label) return ''
      const cleaned = label
        .toLowerCase()
        .replace(/[\s\-\\/]+/g, '_')
        .replace(/[^a-z0-9_\u4e00-\u9fa5]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
      if (!cleaned) return ''
      if (/^[a-z0-9_]+$/.test(cleaned)) return cleaned
      // 中文或混合字段做兜底，避免管理员必须手动输 key
      return `field_${Date.now().toString().slice(-6)}`
    },
    async copyFieldsFromForm(sourceFormId, targetFormId) {
      const sourceFields = await getFormFields(sourceFormId)
      if (!sourceFields || !sourceFields.length) return
      for (let i = 0; i < sourceFields.length; i += 1) {
        const item = sourceFields[i]
        const payload = {
          fieldKey: item.fieldKey || this.buildFieldKey(item.label),
          label: item.label,
          fieldType: item.fieldType,
          requiredField: !!item.requiredField,
          optionsJson: item.optionsJson || '',
          sortOrder: item.sortOrder ?? i * 10,
          validationJson: item.validationJson || '',
          statusCode: item.statusCode || 'ENABLED',
          remark: item.remark || ''
        }
        await createFormField(targetFormId, payload)
      }
    },
    async onDeleteField(item) {
      if (!await adminConfirm(`确定删除字段 ${item.label} 吗？`)) return
      try { await deleteFormField(item.id); await this.loadFields(this.currentForm) } catch (e) { this.errorMsg = e.message || '删除失败' }
    }
  }
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; }
.card > h2 { margin: 0 0 8px; font-size: 18px; font-weight: 700; color: #101828; }
.field-card > h3 { margin: 0 0 10px; font-size: 16px; font-weight: 700; color: #101828; }
.tip { margin: 8px 0 12px; background: #f5f8ff; border: 1px solid #dae5ff; color: #2a4b8d; padding: 10px 12px; border-radius: 8px; font-size: 13px; }
.field-card { margin-top: 16px; border-top: 1px solid #eef2f7; padding-top: 12px; }
.top-config-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
}
.group-card { margin: 0; padding: 12px; border: 1px solid #dbe5fb; border-radius: 10px; background: #f8fbff; min-height: 220px; font-size: 13px; }
.group-head h4 { margin: 0; color: #1f3b73; font-size: 13px; font-weight: 600; }
.group-head small { display: block; margin-top: 4px; color: #6b7a99; font-size: 12px; }
.group-actions { margin-top: 10px; display: flex; gap: 8px; }
.group-actions input { flex: 1; font-size: 13px; }
.group-list { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.group-item { display: flex; align-items: center; justify-content: space-between; gap: 10px; background: #fff; border: 1px solid #e4ebfb; border-radius: 8px; padding: 6px 10px; }
.group-item-title { font-size: 13px; font-weight: 600; color: #2a426f; min-width: 0; flex: 1; }
.group-rename-input {
  flex: 1;
  min-width: 0;
  height: 32px;
  border: 1px solid #c5d4f0;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 13px;
  color: #2a426f;
}
.group-item-ops { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.group-empty { margin-top: 8px; color: #8a97b2; font-size: 12px; }
.field-filters {
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}
.field-filters-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  width: 100%;
}
.field-filters-bulk {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
  padding-top: 12px;
  border-top: 1px solid #edf0f5;
}
.field-filters .flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
}
.field-filters .flt-input--wide {
  width: 280px;
}
.field-filters .flt-select {
  width: 170px;
}
.field-filters :deep(.fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
.table-add-row {
  margin-top: 8px;
  width: 100%;
}
.add-icon-btn {
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px dashed #9db8ff;
  background: #f4f7ff;
  color: #2f6af6;
  font-size: 14px;
  font-weight: 600;
  line-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all .15s ease;
}
.add-icon-btn:hover {
  background: #eaf0ff;
  border-color: #7fa0ff;
  box-shadow: 0 6px 16px rgba(47, 106, 246, 0.18);
}
.add-icon-btn:disabled {
  opacity: .45;
  cursor: not-allowed;
  box-shadow: none;
}
.field-filters-bulk :deep(.fs-btn) {
  height: 40px;
  border-radius: 8px;
}
.bulk-tip { color: #667085; font-size: 12px; white-space: nowrap; flex: 0 0 auto; }
.col-check { width: 36px; text-align: center; }
.bulk-select { min-width: 260px; }
.bulk-select {
  min-width: 240px;
  flex: 0 0 240px;
}
.bulk-btn {
  min-width: 104px;
  height: 40px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.group-actions input { height: 40px; box-sizing: border-box; }
input:not([type="checkbox"]):not([type="file"]) { height: 40px; box-sizing: border-box; }
.cascade-editor {
  border: 1px solid #e4ebfb;
  border-radius: 10px;
  padding: 10px;
  background: #f8fbff;
}
.cascade-editor-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}
.cascade-editor-toolbar--bottom {
  margin-bottom: 0;
  margin-top: 4px;
}
.cascade-group {
  border: 1px solid #dbe5fb;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 10px;
  background: #fff;
}
.cascade-group-head {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  justify-content: space-between;
  flex-wrap: wrap;
}
.cascade-group-main {
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cascade-desc {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #667085;
}
.cascade-desc input {
  font-size: 13px;
  color: #344054;
}
.cascade-group-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.cascade-parent {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #667085;
}
.cascade-child-block {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e4ebfb;
}
.cascade-child-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #667085;
  margin-bottom: 8px;
}
.cascade-child-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.cascade-child-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  min-width: 140px;
}
.cascade-child-head .cascade-child-actions { min-width: 140px; }
.move-cell { white-space: nowrap; }
.col-center { text-align: center; vertical-align: middle; }
.col-tight { padding-left: 4px !important; padding-right: 4px !important; }
.drag-hint { color: #667085; font-size: 12px; user-select: none; }
.row-draggable { cursor: grab; }
.row-draggable.row-dragging { opacity: 0.55; cursor: grabbing; }
.row-draggable td { transition: background-color .12s ease; }
.row-draggable:hover td { background: #f7faff; }
.err-under { display: block; margin-top: 6px; color: #d92d20; font-size: 12px; }
.btn-mini { padding: 4px 8px; font-size: 12px; }
.field-id-tip {
  margin: 10px 0 6px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.55;
  color: #475467;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}
.field-id-tip code {
  font-size: 11px;
  padding: 1px 5px;
  background: #eef2f7;
  border-radius: 4px;
}
table { width: 100%; border-collapse: collapse; margin-top: 8px; }
th, td {
  border-bottom: 1px solid #edf0f5;
  padding: 8px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
}
.col-ops { white-space: nowrap; }
.btn { border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn + .btn { margin-left: 8px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.error { color: #d92d20; }
.ok { color: #067647; }
.fixed-init-card {
  margin: 10px 0 12px;
  padding: 12px;
  border: 1px solid #fecdca;
  background: #fff6f5;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.fixed-init-text {
  margin: 0;
  font-size: 13px;
  color: #912018;
}
.dialog {
  width: min(760px, 100%);
  max-height: min(92vh, 900px);
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.dialog-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 4px;
}
.dialog .actions { flex-shrink: 0; margin-top: 12px; }
.export-card {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #e6eaf2;
  border-radius: 10px;
  background: #fafbff;
  min-height: 220px;
}
.export-card h4 { margin: 0 0 8px; font-size: 13px; color: #1d2939; font-weight: 600; }
.export-tip { margin: 0 0 10px; font-size: 12px; color: #667085; line-height: 1.5; }
.export-tip code { background: #eef2f7; padding: 1px 6px; border-radius: 4px; font-size: 11px; }
.export-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; margin-bottom: 8px; font-size: 13px; }
.export-label { font-weight: 600; color: #344054; min-width: 4.5em; }
.export-val { color: #475467; flex: 1; min-width: 120px; word-break: break-all; }
.export-file { position: relative; overflow: hidden; cursor: pointer; }
.export-file-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; font-size: 0; }
@media (max-width: 1200px) {
  .top-config-grid {
    grid-template-columns: 1fr;
  }
}
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.form-code-hint { grid-column: span 2; margin: 0; padding: 10px 12px; background: #f2f4f7; border-radius: 8px; color: #667085; font-size: 13px; line-height: 1.5; }
.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; }
.field > span { font-weight: 600; }
.field-hint { color: #98a2b3; font-size: 12px; }
.full { grid-column: span 2; }
textarea { min-height: 90px; resize: vertical; }
input, select, textarea { border: 1px solid #d0d7e2; border-radius: 8px; padding: 8px 10px; }
</style>

